import { useRef, useEffect, useState, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X, Send, Trash2, Loader2, HelpCircle } from 'lucide-react';
import { useAI } from '../contexts/AIContext';
import { aiAssistant } from '../lib/api';

const ROUTE_LABELS: Record<string, string> = {
  '/pages': 'Pages',
  '/media': 'Media',
  '/design': 'Design',
  '/contact': 'Contact',
  '/settings': 'Settings',
  '/revisions': 'Revisions',
  '/sites': 'Sites',
  '/domains': 'Domains',
};

const HELP_SUGGESTIONS = [
  "Create a new page",
  "How do I upload images?",
  "Where can I change my site design?",
  "How do I publish a page?",
];

export default function GlobalAIChat() {
  const {
    isOpen,
    messages,
    context,
    isLoading,
    toggleChat,
    closeChat,
    addMessage,
    clearHistory,
    setIsLoading,
    activeWizardId,
    setActiveWizardId,
  } = useAI();

  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setShowHelp(false);

    // Add user message
    addMessage({
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
    });

    setIsLoading(true);

    try {
      // Call AI assistant API
      const result = await aiAssistant.chat({
        message: userMessage,
        context,
        activeWizardId,
      });

      // Add AI response
      addMessage({
        role: 'assistant',
        content: result.response,
        timestamp: new Date(),
        actions: result.actions,
        metadata: result.metadata,
      });

      // Update active wizard ID if creating a page
      if (result.wizardId) {
        setActiveWizardId(result.wizardId);
      }

      // If page was created, show success and optionally navigate
      if (result.pageCreated && result.pageId) {
        // Optionally auto-navigate after a delay
        // setTimeout(() => navigate(`/pages/${result.pageId}/edit`), 2000);
      }

    } catch (error: any) {
      console.error('AI chat error:', error);
      addMessage({
        role: 'assistant',
        content: error.message || "Sorry, I encountered an error. Please try again.",
        timestamp: new Date(),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleActionClick = (action: any) => {
    if (action.action === 'navigate' && action.path) {
      navigate(action.path);
      if (action.label.includes('Close')) {
        closeChat();
      }
    } else if (action.action === 'dismiss') {
      // Just dismiss
    } else if (action.handler) {
      action.handler();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    inputRef.current?.focus();
    setShowHelp(false);
  };

  const handleClearHistory = () => {
    if (confirm('Clear chat history? This cannot be undone.')) {
      clearHistory();
      if (activeWizardId) {
        // Cleanup wizard session on backend
        aiAssistant.cleanup(activeWizardId).catch(console.error);
        setActiveWizardId(null);
      }
    }
  };

  const currentRouteLabel = ROUTE_LABELS[context.path] || 'Dashboard';

  return (
    <>
      {/* Floating Action Button */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 flex items-center justify-center z-40 group"
          aria-label="Open AI Assistant"
        >
          <Sparkles className="w-6 h-6 text-white" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse border-2 border-white"></span>
        </button>
      )}

      {/* Chat Drawer */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity"
            onClick={closeChat}
          />

          {/* Drawer */}
          <div className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col animate-slide-in-right">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-primary-500 to-primary-600">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 backdrop-blur rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">AI Assistant</h2>
                  <p className="text-xs text-primary-100">Always here to help</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleClearHistory}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                  aria-label="Clear history"
                  title="Clear chat history"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <button
                  onClick={closeChat}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                  aria-label="Close chat"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Context Pills */}
            <div className="px-4 py-3 bg-gray-50 border-b flex items-center gap-2 flex-wrap">
              <div className="text-xs bg-white border border-gray-200 rounded-full px-3 py-1 flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-primary-500"></div>
                <span className="font-medium text-gray-700">{currentRouteLabel}</span>
              </div>
              {context.siteName && (
                <div className="text-xs bg-white border border-gray-200 rounded-full px-3 py-1">
                  <span className="text-gray-500">Site:</span>{' '}
                  <span className="font-medium text-gray-700">{context.siteName}</span>
                </div>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-primary-500 text-white'
                        : msg.role === 'system'
                        ? 'bg-amber-50 text-amber-900 border border-amber-200'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div className="whitespace-pre-wrap text-sm leading-relaxed">
                      {msg.content}
                    </div>

                    {/* Action buttons */}
                    {msg.actions && msg.actions.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {msg.actions.map((action, actionIdx) => (
                          <button
                            key={actionIdx}
                            onClick={() => handleActionClick(action)}
                            className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                              action.variant === 'primary'
                                ? 'bg-primary-500 text-white hover:bg-primary-600'
                                : action.variant === 'danger'
                                ? 'bg-red-500 text-white hover:bg-red-600'
                                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}

                    <div
                      className={`text-xs mt-1 ${
                        msg.role === 'user'
                          ? 'text-primary-100'
                          : msg.role === 'system'
                          ? 'text-amber-600'
                          : 'text-gray-500'
                      }`}
                    >
                      {msg.timestamp.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </div>
                </div>
              ))}

              {/* Loading indicator */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
                      <span className="text-sm text-gray-600">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Help Suggestions */}
            {showHelp && (
              <div className="px-4 py-3 border-t bg-gray-50">
                <p className="text-xs font-medium text-gray-600 mb-2">Try asking:</p>
                <div className="space-y-1.5">
                  {HELP_SUGGESTIONS.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="w-full text-left text-xs px-3 py-2 bg-white border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="border-t p-4 bg-white">
              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => setShowHelp(!showHelp)}
                  className="text-gray-400 hover:text-primary-500 transition-colors p-1"
                  title="Show help suggestions"
                >
                  <HelpCircle className="w-5 h-5" />
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask me anything..."
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  disabled={isLoading}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!input.trim() || isLoading}
                  className="bg-primary-500 text-white px-4 py-2.5 rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Press Enter to send • Context-aware
              </p>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
